import { useState } from "react";
import { Platform, useWindowDimensions, ScaledSize } from "react-native";

/**
 * A hook that provides stable window dimensions on web when the virtual keyboard appears.
 * On mobile web browsers, the keyboard opening causes the window height to shrink,
 * which can cause unwanted layout shifts. This hook detects keyboard-induced resizes
 * and returns the original dimensions instead.
 *
 * On native platforms, this behaves identically to useWindowDimensions().
 *
 * @returns ScaledSize with stable width/height on web when keyboard is open
 */
export function useStableWindowDimensions(): ScaledSize {
  const currentDimensions = useWindowDimensions();
  const [stableDimensions, setStableDimensions] =
    useState<ScaledSize>(currentDimensions);
  // Baseline ("max") dimensions used to detect keyboard-induced shrink on web.
  // Held as state (not a ref) so it can be read/updated during render under the
  // change-guard below without tripping the rules-of-refs lint.
  const [maxDimensions, setMaxDimensions] = useState({
    height: currentDimensions.height,
    width: currentDimensions.width,
  });
  const [prevDimensions, setPrevDimensions] = useState(currentDimensions);

  // Run the stability logic during render (the "adjust state during render"
  // pattern) whenever the window dimensions change, instead of in an effect.
  if (prevDimensions !== currentDimensions) {
    setPrevDimensions(currentDimensions);

    if (Platform.OS !== "web") {
      // On native platforms, always use current dimensions.
      setStableDimensions(currentDimensions);
    } else {
      // On web, detect if this is a keyboard opening (height reduction) or a
      // legitimate window resize.
      const heightDifference = maxDimensions.height - currentDimensions.height;
      const widthDifference = Math.abs(
        maxDimensions.width - currentDimensions.width,
      );

      // If height decreased significantly (>200px) but width stayed similar
      // (<50px change), it's likely the keyboard opening - keep stable
      // dimensions. Otherwise it's a legitimate resize (desktop window resize,
      // rotation, etc.): reset BOTH max dimensions to the current values so
      // rotation scenarios work correctly, and adopt the new dimensions.
      if (!(heightDifference > 200 && widthDifference < 50)) {
        setMaxDimensions({
          height: currentDimensions.height,
          width: currentDimensions.width,
        });
        setStableDimensions(currentDimensions);
      }
    }
  }

  return stableDimensions;
}
